import React, { useState } from 'react';
import { Calculator, Droplet, Car, DollarSign, Calendar, Clock } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент с дополнительными калькуляторами для расчета расхода топлива
 */
const AdditionalCalculators = ({ results, formData }) => {
  const [annualDistance, setAnnualDistance] = useState('15000');
  const [yearsOwnership, setYearsOwnership] = useState('5');
  const [maintenanceCost, setMaintenanceCost] = useState('30000');
  const [insuranceCost, setInsuranceCost] = useState('15000');
  const [taxCost, setTaxCost] = useState('3500');
  
  if (!results) return null;

  // Расчет годовых затрат на топливо
  const calculateAnnualFuelCost = () => {
    const distance = parseFloat(annualDistance);
    if (isNaN(distance) || distance <= 0) return 0;
    
    return (distance / 100) * results.fuelConsumptionWithEco * results.fuelPrice;
  };
  
  // Расчет месячных затрат на топливо
  const calculateMonthlyFuelCost = () => {
    return calculateAnnualFuelCost() / 12;
  };

  // Расчет стоимости владения
  const calculateTotalOwnershipCost = () => {
    const years = parseFloat(yearsOwnership);
    const distance = parseFloat(annualDistance);
    const maintenance = parseFloat(maintenanceCost);
    const insurance = parseFloat(insuranceCost);
    const tax = parseFloat(taxCost);
    
    if (isNaN(years) || years <= 0 || isNaN(distance) || distance <= 0) return 0;
    
    const annualFuelCost = calculateAnnualFuelCost();
    const totalMaintenanceCost = maintenance * years;
    const totalInsuranceCost = insurance * years;
    const totalTaxCost = tax * years;
    
    return (annualFuelCost * years) + totalMaintenanceCost + totalInsuranceCost + totalTaxCost;
  };
  
  // Расчет стоимости за 1 км
  const calculateCostPerKm = () => {
    const years = parseFloat(yearsOwnership);
    const distance = parseFloat(annualDistance);
    
    if (isNaN(years) || years <= 0 || isNaN(distance) || distance <= 0) return 0;
    
    const totalOwnershipCost = calculateTotalOwnershipCost();
    const totalDistance = distance * years;
    
    return totalOwnershipCost / totalDistance;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-blue-500" />
        Дополнительные расчеты
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Годовые затраты на топливо */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-blue-500" />
            Годовые затраты на топливо
          </h4>
          
          <div className="mb-4">
            <label htmlFor="annualDistance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Годовой пробег (км)
            </label>
            <input
              type="text"
              id="annualDistance"
              value={annualDistance}
              onChange={(e) => setAnnualDistance(e.target.value)}
              className="form-input w-full"
              placeholder="Например: 15000"
            />
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Годовые затраты:</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(calculateAnnualFuelCost())}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Ежемесячно:</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(calculateMonthlyFuelCost())}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
            Расчет основан на вашем текущем расходе топлива {formatNumber(results.fuelConsumptionWithEco, 1)} л/100км 
            и цене топлива {formatNumber(results.fuelPrice, 2)} ₽/л.
          </div>
        </div>

        {/* Стоимость владения автомобилем */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <Car className="w-4 h-4 mr-2 text-blue-500" />
            Стоимость владения автомобилем
          </h4>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label htmlFor="yearsOwnership" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Лет владения
              </label>
              <input
                type="text"
                id="yearsOwnership"
                value={yearsOwnership}
                onChange={(e) => setYearsOwnership(e.target.value)}
                className="form-input w-full"
                placeholder="Например: 5"
              />
            </div>
            <div>
              <label htmlFor="maintenanceCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                ТО в год (₽)
              </label>
              <input
                type="text"
                id="maintenanceCost"
                value={maintenanceCost}
                onChange={(e) => setMaintenanceCost(e.target.value)}
                className="form-input w-full"
                placeholder="Например: 30000"
              />
            </div>
            <div>
              <label htmlFor="insuranceCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Страховка в год (₽)
              </label>
              <input
                type="text"
                id="insuranceCost"
                value={insuranceCost}
                onChange={(e) => setInsuranceCost(e.target.value)}
                className="form-input w-full"
                placeholder="Например: 15000"
              />
            </div>
            <div>
              <label htmlFor="taxCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Налог в год (₽)
              </label>
              <input
                type="text"
                id="taxCost"
                value={taxCost}
                onChange={(e) => setTaxCost(e.target.value)}
                className="form-input w-full"
                placeholder="Например: 3500"
              />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Общая стоимость:</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(calculateTotalOwnershipCost())}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Стоимость 1 км:</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatNumber(calculateCostPerKm(), 2)} ₽
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
            Стоимость владения включает затраты на топливо, техническое обслуживание, страхование и налоги 
            за указанный период времени и пробег.
          </div>
        </div>
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <DollarSign className="w-5 h-5 mt-0.5 mr-3 text-blue-500" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Полезно знать</h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              В России средний годовой пробег составляет около 15,000-20,000 км. Средние затраты на владение 
              автомобилем составляют 10-12 ₽ на километр для бюджетных моделей, 15-25 ₽ для автомобилей 
              среднего класса и 30-50 ₽ для премиальных автомобилей.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalCalculators;